import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { I18nService } from '@localize/services';
import {
	AppCluster,
	AppKeyFeaturesMask,
	AppKeyProvider,
	AppKeyService,
	IAppKey,
	IAppKeyFeaturesMask,
	IAppKeyUpdate
} from '@services/appkey/';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ProgressIndicatorService } from '@services/progress';
import { deflateMask, FormValidators, inflateMask } from '@tcp13equals2/z5-lib';
import { finalize } from 'rxjs/operators';

import { CompanyService, ICompany } from '../../../../../../services/company';
import { FormEditorInputType } from '../../../../../../shared/components';
import { AppKeyEditFormEvent } from './appkey-edit-form.models';

@Component({
	selector: 'app-appkey-edit-form',
	templateUrl: './appkey-edit-form.component.html',
	styleUrls: ['./appkey-edit-form.component.scss']
})
export class AppKeyEditFormComponent implements OnInit {
	@Input() company: ICompany;
	@Input() appKey: IAppKey;
	@Input() cluster: AppCluster;
	@Input() enableSuspend = true;
	@Input() enableName = true;
	@Input() unsavedChanges = false;
	@Input() confirmUpdate = true;
	@Input() closeLabel = this.i18nService.get('Close', 'app.descriptor.close');
	@Input() cancelLabel = this.i18nService.get('Cancel', 'app.descriptor.cancel');

	@Output() updated = new EventEmitter<AppKeyEditFormEvent>();
	@Output() closed = new EventEmitter<AppKeyEditFormEvent>();
	@Output() cancelled = new EventEmitter<AppKeyEditFormEvent>();
	@Output() unsavedChangesChange = new EventEmitter<boolean>();
	@Output() appKeyChange = new EventEmitter<IAppKey>();

	public clientSecret = '';
	public secretVisible = false;
	public appKeyProviders = AppKeyProvider;
	public formEditorInputType = FormEditorInputType;
	public editable = true;
	public clusterFeaturesMask: IAppKeyFeaturesMask;

	// Accept localhost and loopback URLs for local testing
	private readonly localUrlReg = /^https?:\/\/(?:localhost|127(?:\.\d{1,3}){3})(?::\d{1,5})?(?:[/?#]\S*)?$/i;

	public errorStateMatcher = new ErrorStateMatcher();
	public formInputs = new FormGroup({
		name: new FormControl(null, [Validators.required, this.formsService.appNameValidator()]),
		sigSecret: new FormControl(null),
		redirectUri: new FormControl(null),
		refreshTokenValidityDays: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(3650)]),
		refreshTokenValidityMinutes: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(3650 * 24 * 60)]),
		accessTokenValidityDays: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(1)]),
		accessTokenValidityMinutes: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(24 * 60)]),
		emailVerifyUrl: new FormControl('', [control =>  this.localUrlReg.test(control.value ?? '') ? null : FormValidators.url(control)]),
		passwordResetUrl: new FormControl('', [control =>  this.localUrlReg.test(control.value ?? '') ? null : FormValidators.url(control)]),
		denyLoginEmailUnverified: new FormControl(null),
		denyLoginUnacceptedTerms: new FormControl(null),
		refreshRequiresTerms: new FormControl(null),
		encryptJwt: new FormControl(null),
		laxPasswordComplexity: new FormControl(null),
		haveIBeenPwnedPasswordComplexity: new FormControl(null),
		autoAcceptTerms: new FormControl(null),
		laxLoginSecret: new FormControl(null),
		z5JWT: new FormControl(null),
		noNullNames: new FormControl(null),
		loginMfaEnabled: new FormControl(null),
		loginMfaEnforced: new FormControl(null)
	});

	constructor(
		private appkeyService: AppKeyService,
		private i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private formsService: FormsService,
		private companyService: CompanyService
	) {}

	public ngOnInit(): void {
		this.editable = this.companyService.isAdminContact();

		// Convert cluster Bitmask to get the configurable options available on cluster
		this.clusterFeaturesMask = inflateMask(this.cluster.featuresMask, AppKeyFeaturesMask) as any;

		const accessTTL = this.getDaysAndMinutes(this.appKey.accessTokenExpiration);
		const refreshTTL = this.getDaysAndMinutes(this.appKey.refreshTokenExpiration);

		while (this.clientSecret.length < this.appKey.clientId.length) {
			this.clientSecret += '•';
		}

		this.formInputs.patchValue({
			name: this.appKey.name,
			sigSecret: this.appKey.sigSecret,
			refreshTokenValidityDays: refreshTTL.days,
			refreshTokenValidityMinutes: refreshTTL.minutes,
			accessTokenValidityDays: accessTTL.days,
			accessTokenValidityMinutes: accessTTL.minutes,
			emailVerifyUrl: this.appKey.emailVerifyUrl,
			passwordResetUrl: this.appKey.passwordResetUrl,
			redirectUri: this.appKey.redirectUri,
			...inflateMask(this.appKey.featuresMask, AppKeyFeaturesMask)
		});

		this.subscribeToTokenTTLUpdates();

		if (!this.editable) {
			this.formInputs.disable();
		}

		this.formInputs.valueChanges.subscribe(() => this.hasChanges());
	}

	public cancel(): void {
		this.cancelled.emit({ appKey: null });
	}

	public close(): void {
		this.closed.emit({ appKey: null });
	}

	public suspendAppKey(): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Are you sure?', 'app.prompt.appkey.delete.confirm-message'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.progressIndicatorService.spinner(true);
					this.appkeyService
						.update(this.appKey.clientId, { valid: false })
						.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
						.subscribe(
							(result: IAppKey) => {
								this.appKey = result;
								this.hasChanges();
								this.updated.emit({ appKey: result });
								this.appKeyChange.emit(this.appKey);
							},
							(error: any) => this.feedbackService.apiError(error)
						);
				}
			}
		});
	}

	public showSecret() {
		this.clientSecret = this.appKey.clientSecret;
		this.secretVisible = true;
	}

	public onFormSubmit() {
		if (!this.hasChanges()) return this.close();

		if (this.confirmUpdate) {
			this.feedbackService.confirm({
				title: FeedbackTitle.attention,
				message: this.i18nService.get(
					'Updating these settings will affect any application using the key, are you sure you want to continue?',
					'app.prompt.appkey.confirm-save'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.saveAppKey();
				}
			});
		} else {
			this.saveAppKey();
		}
	}

	private hasChanges(): boolean {
		this.unsavedChanges = Object.keys(this.getFormData()).length > 0;

		this.unsavedChangesChange.emit(this.unsavedChanges);

		return this.unsavedChanges;
	}

	private getFormData(): IAppKeyUpdate {
		const data = {};

		for (const [controlName, control] of Object.entries(this.formInputs.controls)) {
			if (!control.dirty) continue;

			switch (controlName) {
				case 'denyLoginEmailUnverified':
				case 'denyLoginUnacceptedTerms':
				case 'encryptJwt':
				case 'laxPasswordComplexity':
				case 'haveIBeenPwnedPasswordComplexity':
				case 'autoAcceptTerms':
				case 'laxLoginSecret':
				case 'z5JWT':
				case 'refreshRequiresTerms':
				case 'noNullNames':
				case 'loginMfaEnabled':
				case 'loginMfaEnforced':
					const {
						denyLoginEmailUnverified,
						denyLoginUnacceptedTerms,
						encryptJwt,
						laxPasswordComplexity,
						haveIBeenPwnedPasswordComplexity,
						autoAcceptTerms,
						laxLoginSecret,
						z5JWT,
						refreshRequiresTerms,
						noNullNames,
						loginMfaEnabled,
						loginMfaEnforced
					} = this.formInputs.value;
					const featuresMask = deflateMask(
						{
							denyLoginEmailUnverified,
							denyLoginUnacceptedTerms,
							encryptJwt,
							laxPasswordComplexity,
							haveIBeenPwnedPasswordComplexity,
							autoAcceptTerms,
							laxLoginSecret,
							z5JWT,
							refreshRequiresTerms,
							noNullNames,
							loginMfaEnabled,
							loginMfaEnforced
						},
						AppKeyFeaturesMask
					);

					if (featuresMask !== this.appKey.featuresMask) data['featuresMask'] = featuresMask;

					break;
				case 'accessTokenValidityDays':
				case 'accessTokenValidityMinutes':
					const accessTokenExpiration =
						this.formInputs.value.accessTokenValidityDays * 24 * 60 + this.formInputs.value.accessTokenValidityMinutes;

					if (accessTokenExpiration !== this.appKey.accessTokenExpiration) data['accessTokenExpiration'] = accessTokenExpiration;

					break;
				case 'refreshTokenValidityDays':
				case 'refreshTokenValidityMinutes':
					const refreshTokenExpiration =
						this.formInputs.value.refreshTokenValidityDays * 24 * 60 + this.formInputs.value.refreshTokenValidityMinutes;

					if (refreshTokenExpiration !== this.appKey.refreshTokenExpiration)
						data['refreshTokenExpiration'] = refreshTokenExpiration;

					break;
				default:
					const value = typeof control.value === 'string' ? control.value.trim() : control.value;

					if (
						(this.appKey.hasOwnProperty(controlName) && this.appKey[controlName] !== value) ||
						(!this.appKey.hasOwnProperty(controlName) && value)
					) {
						data[controlName] = value;
					}
			}
		}


		return data;
	}

	private saveAppKey(): void {
		this.progressIndicatorService.spinner(true);

		this.appkeyService
			.update(this.appKey.clientId, this.getFormData())
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(result: IAppKey) => {
					this.appKey = result;
					this.hasChanges();
					this.updated.emit({ appKey: result });
					this.appKeyChange.emit(this.appKey);
				},
				(error: any) => this.feedbackService.apiError(error)
			);
	}

	private validateTokenTTL(
		refreshTokenValidityDays: AbstractControl,
		refreshTokenValidityMinutes: AbstractControl,
		accessTokenValidityDays: AbstractControl,
		accessTokenValidityMinutes: AbstractControl
	) {
		const refreshTokenTTL = refreshTokenValidityDays.value * 24 * 60 + refreshTokenValidityMinutes.value;
		const accessTokenTTL = accessTokenValidityDays.value * 24 * 60 + accessTokenValidityMinutes.value;

		let refreshError = null;
		let accessError = null;

		// Validate refresh token is between 60 and (3650 * 24 * 60) minutes
		if (refreshTokenTTL < 60 || refreshTokenTTL > 3650 * 24 * 60) {
			refreshError = { invalidTTL: true };
			refreshTokenValidityDays.setErrors(refreshError);
			refreshTokenValidityMinutes.setErrors(refreshError);
		} else {
			refreshTokenValidityDays.setErrors(refreshError);
			refreshTokenValidityMinutes.setErrors(refreshError);
		}

		// Access token must be between 5 and (24 * 60) minutes
		if (accessTokenTTL < 5 || accessTokenTTL > 24 * 60) {
			accessError = { invalidTTL: true };
			accessTokenValidityDays.setErrors(accessError);
			accessTokenValidityMinutes.setErrors(accessError);
		} else {
			accessTokenValidityDays.setErrors(accessError);
			accessTokenValidityMinutes.setErrors(accessError);
		}

		// - Refresh and Access token TTLs must be valid before this validation makes sense
		// - Access token TTL can't be larger than refresh token TTL
		if (!refreshError && !accessError && accessTokenTTL > refreshTokenTTL) {
			accessError = { accessLargerThanRefreshTTL: true };
			accessTokenValidityDays.setErrors(accessError);
			accessTokenValidityMinutes.setErrors(accessError);
		}
	}

	private getDaysAndMinutes(min: number) {
		const days = Math.floor(min / 1440);
		const minutes = Math.floor(min - days * 1440);
		return { days, minutes };
	}

	private subscribeToTokenTTLUpdates() {
		const formControls = {
			refreshTokenValidityMinutes: this.formInputs.get('refreshTokenValidityMinutes'),
			refreshTokenValidityDays: this.formInputs.get('refreshTokenValidityDays'),
			accessTokenValidityMinutes: this.formInputs.get('accessTokenValidityMinutes'),
			accessTokenValidityDays: this.formInputs.get('accessTokenValidityDays')
		};

		for (const control of Object.values(formControls)) {
			control.valueChanges.subscribe((value) => {
				// Format to integer
				value = parseInt(value + '', 10);
				value = isNaN(value) ? 0 : value;
				control.setValue(value, { emitEvent: false });

				this.validateTokenTTL(
					formControls.refreshTokenValidityDays,
					formControls.refreshTokenValidityMinutes,
					formControls.accessTokenValidityDays,
					formControls.accessTokenValidityMinutes
				);
			});
		}
	}
}
